import React from 'react';
import { MS_TOPICS } from '../../messages/mockData';
import type { MsMessage, MsThread } from '../../messages/types';
import { Icon } from '../Icon/Icon';

function Delivery({ status }: { status: string }) {
  if (status === 'sent') {
    return (
      <span title="Sent — accepted by carrier" style={{ color: 'var(--ink-4)', display: 'inline-flex' }}>
        <Icon kind="check" size={11} stroke={2.6} />
      </span>
    );
  }
  if (status === 'delivered') {
    return (
      <span title="Delivered" style={{ color: 'var(--success)', display: 'inline-flex' }}>
        <Icon kind="check" size={11} stroke={2.6} style={{ marginRight: -5 }} />
        <Icon kind="check" size={11} stroke={2.6} />
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span
        title="Delivery failed"
        style={{
          color: 'var(--terracotta)',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 2,
          fontWeight: 700,
          fontSize: 10,
        }}
      >
        <Icon kind="alert" size={11} stroke={2.4} />
        Failed
      </span>
    );
  }
  return null;
}

function senderInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0]![0] ?? ''}${parts[1]![0] ?? ''}`.toUpperCase();
  return (parts[0]?.slice(0, 2) ?? '??').toUpperCase();
}

/** Message bubble — matches reference `MsgBubble` in Elva Messages (standalone).html */
export function MessagesMsgBubble({
  message: m,
  thread,
  currentStaffName,
}: {
  message: MsMessage;
  thread: MsThread;
  currentStaffName?: string;
}) {
  const isPatient = m.who === 'patient';
  const isElva = m.who === 'elva';
  const isStaff = m.who === 'staff';
  const topic = m.topic ? MS_TOPICS[m.topic] : undefined;
  const staffLabel = m.senderName?.trim() || currentStaffName?.trim() || '';

  if (m.who === 'elva_held') {
    return (
      <div className="ms-held be-pop">
        <Icon kind="pause" size={14} style={{ color: 'var(--warn-amber)', flexShrink: 0, marginTop: 1 }} />
        <div>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--warn-amber)', marginBottom: 2 }}>
            Elva paused on this message
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-2)', lineHeight: 1.5 }}>{m.heldReason}</div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 9,
        justifyContent: isPatient ? 'flex-start' : 'flex-end',
      }}
    >
      {isPatient ? (
        <span className="ms-av ms-av-patient" style={{ width: 28, height: 28, fontSize: 10.5, marginBottom: 20 }}>
          {thread.initials}
        </span>
      ) : null}

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isPatient ? 'flex-start' : 'flex-end',
          maxWidth: '76%',
          minWidth: 0,
        }}
      >
        {!isPatient && (isElva || staffLabel) ? (
          <div className="ms-ribbon" style={{ marginBottom: 4, color: isElva ? 'var(--ai-3)' : 'var(--ink-3)' }}>
            <Icon kind={isElva ? 'sparkle' : 'headphones'} size={11} stroke={2} />
            {isElva ? 'Elva · AI' : staffLabel}
          </div>
        ) : null}

        <div className={`ms-bubble ${isPatient ? 'patient' : isElva ? 'elva' : 'staff'}`}>
          {isPatient && m.textTranslated ? (
            <>
              <div>{m.textTranslated}</div>
              <div
                style={{
                  fontSize: 11.5,
                  color: 'var(--ink-4)',
                  marginTop: 6,
                  paddingTop: 6,
                  borderTop: '1px dashed var(--cream-2)',
                  fontStyle: 'italic',
                  lineHeight: 1.45,
                }}
              >
                <Icon kind="translate" size={10} style={{ marginRight: 4, verticalAlign: '-1px' }} />
                {m.text}
              </div>
            </>
          ) : (
            <div>{m.text}</div>
          )}

          {m.attachments?.map((a, i) => (
            <div
              key={i}
              style={{
                marginTop: 8,
                paddingTop: 8,
                borderTop: '1px solid var(--cream-2)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 12,
              }}
            >
              <Icon kind="image" size={13} style={{ color: 'var(--ai-3)' }} />
              <span style={{ fontWeight: 600, color: 'var(--ink-1)' }}>{a.name}</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-4)' }}>{a.size}</span>
            </div>
          ))}

          {isElva && m.textOriginal ? (
            <div
              style={{
                fontSize: 11.5,
                color: 'var(--ink-4)',
                marginTop: 6,
                paddingTop: 6,
                borderTop: '1px dashed color-mix(in srgb, var(--ai-3) 30%, transparent)',
                fontStyle: 'italic',
                lineHeight: 1.45,
              }}
            >
              <Icon kind="translate" size={10} style={{ marginRight: 4, verticalAlign: '-1px' }} />
              Sent as: {m.textOriginal}
            </div>
          ) : null}
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginTop: 5,
            padding: '0 3px',
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--ink-4)' }}>{m.time}</span>
          {(isElva || isStaff) && m.deliveryStatus ? (
            <>
              <span style={{ color: 'var(--ink-4)' }}>·</span>
              <Delivery status={m.deliveryStatus} />
            </>
          ) : null}
          {topic ? (
            <>
              <span style={{ color: 'var(--ink-4)' }}>·</span>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  fontSize: 10,
                  fontWeight: 600,
                  color: topic.color,
                }}
              >
                <span style={{ width: 5, height: 5, borderRadius: 999, background: topic.color }} />
                {topic.label}
              </span>
            </>
          ) : null}
        </div>
      </div>

      {!isPatient ? (
        <span
          className={`ms-av ${isElva ? 'ms-av-elva' : 'ms-av-staff'}`}
          style={{ width: 28, height: 28, fontSize: 10.5, marginBottom: 20 }}
        >
          {isElva ? <Icon kind="sparkle" size={13} /> : staffLabel ? senderInitials(staffLabel) : null}
        </span>
      ) : null}
    </div>
  );
}
